import { formatNumberByCountry, roundQuantity } from "../../utils/formatNumbers";
import { calculateLineAmounts } from "../../utils/invoiceTotals";
import {
  addMoney,
  moneyEquals,
  resolveMoneyDecimals,
  roundMoney,
  subtractMoney,
} from "../../utils/money";

/**
 * Lo que queda por abonar de una factura, y en qué estado la deja.
 *
 * `getRemainingQuantitiesForInvoice` responde **por producto**, no por línea:
 * agrupa por el número de artículo para que dos notas de crédito sucesivas no
 * devuelvan dos veces lo mismo. Eso está bien, pero deja dos cabos sueltos que
 * este archivo ata, porque son reglas puras y se pueden probar sin base ni
 * pantalla.
 */

/**
 * Cuánto queda por devolver de cada producto de una factura.
 *
 * Se agrupa por número de artículo, no por línea, para que dos notas de crédito
 * sucesivas de la misma factura no puedan devolver dos veces lo mismo.
 *
 * Las cantidades se acumulan: una factura puede traer el **mismo artículo en
 * más de una línea**, y quedarse con la última hacía que una venta de dos
 * unidades pareciera de una. Un abono de una sola unidad dejaba entonces la
 * factura marcada como saldada, con producto todavía por devolver.
 *
 * Vive acá, y no dentro del servicio, porque la usan los dos que tienen que
 * decir lo mismo: el servicio --que lo guarda en la factura-- y el listado
 * --que lo pinta--. Con una copia en cada sitio, se separan.
 *
 * Además de la cantidad se acumula el **importe con IVA**. Contar sólo unidades
 * no basta cuando el mismo artículo va en líneas con precios distintos: medido
 * en Costa Rica DEV, `VFR15-009037` trae `71-2022669` en 1 × 15.730 y
 * 1,5 × 4.856,25, y el segundo abono de 1 × 15.730 (`VNR15-000318`) cabía por
 * cantidad y se pasaba por importe. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H2 · TPV.
 *
 * @param {Array} lineasDeLaFactura Las líneas de la factura original.
 * @param {Array} lineasDeLosAbonos Las líneas de todas sus notas de crédito.
 * @param {object} [country] El país activo, para la precisión del importe. Quien
 *   sólo mira cantidades --el listado, el estado de la devolución-- puede omitirlo.
 * @returns {Object} Por número de artículo: original, ya abonado y disponible,
 *   en cantidad y en importe con IVA.
 */
export const loQueQuedaPorProducto = (lineasDeLaFactura, lineasDeLosAbonos, country = null) => {
  const decimales = resolveMoneyDecimals(country);

  // Las cantidades de un abono son negativas; se leen en positivo para sumarlas.
  const abonado = {};
  for (const linea of lineasDeLosAbonos || []) {
    const clave = linea?.No;
    abonado[clave] = {
      cantidad: roundQuantity((abonado[clave]?.cantidad || 0) + Math.abs(linea?.Quantity || 0)),
      importe: addMoney(abonado[clave]?.importe || 0, importeConIvaDeLaLinea(linea, country), decimales),
    };
  }

  const porProducto = {};
  for (const linea of lineasDeLaFactura || []) {
    const clave = linea?.No;
    const originalQuantity = roundQuantity(
      (porProducto[clave]?.originalQuantity || 0) + Math.abs(linea?.Quantity || 0)
    );
    const originalAmount = addMoney(
      porProducto[clave]?.originalAmount || 0,
      importeConIvaDeLaLinea(linea, country),
      decimales
    );
    const alreadyAbonedQuantity = abonado[clave]?.cantidad || 0;
    const alreadyAbonedAmount = abonado[clave]?.importe || 0;

    porProducto[clave] = {
      originalQuantity,
      alreadyAbonedQuantity,
      remainingQuantity: roundQuantity(originalQuantity - alreadyAbonedQuantity),
      originalAmount,
      alreadyAbonedAmount,
      remainingAmount: subtractMoney(originalAmount, alreadyAbonedAmount, decimales),
    };
  }

  return porProducto;
};

/**
 * El importe con IVA de una línea, en positivo.
 *
 * Con la misma fórmula con que el terminal calcula la línea
 * (`calculateLineAmounts`) para la factura, para sus abonos y para la nota de
 * crédito que se valida: si cada lado usara la suya, un abono exacto se pasaría
 * por un redondeo.
 */
const importeConIvaDeLaLinea = (linea, country) =>
  Math.abs(
    roundMoney(
      calculateLineAmounts({
        unitPrice: linea?.Unit_Price,
        qty: linea?.Quantity,
        discountPct: linea?.Line_Discount_,
        ivaPct: linea?.PorIva,
        priceIncludesVAT: linea?.Price_Includes_VAT,
        country,
      }).total,
      resolveMoneyDecimals(country)
    )
  );

/**
 * Si una nota de crédito quedó fuera porque Business Central la rechazó por
 * `ABONO_EXCEDE`, y por eso no resta de lo que queda por abonar.
 *
 * BC no la cuenta como abonada y no se va a reintentar --el rechazo es
 * definitivo--, así que contarla en el terminal dejaría la factura con menos
 * disponible del real, y la nota de crédito buena que pide el aviso («haz una
 * nota de crédito por lo que queda») no cabría.
 *
 * Se lee del motivo guardado en la fila (`syncErrorMessage`), que lleva el texto
 * de BC. Sólo vale mientras siga fuera de la cola y sin número: si alguien la
 * corrige y vuelve a quedar pendiente, o si llegó a registrarse, cuenta.
 *
 * El código va escrito aquí y no importado de `rechazoDefinitivo.js` porque ese
 * archivo importa `invoiceService`, que importa éste. Acuerdo
 * `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H2 · TPV.
 *
 * @param {object} abono La cabecera local de la nota de crédito.
 */
export const abonoRechazadoPorExceso = (abono) => {
  if (!abono || abono.No) return false;
  if (abono.pendingSync === 1 || abono.pendingSync === true) return false;
  return /ABONO_EXCEDE/.test(String(abono.syncErrorMessage ?? ""));
};

/** En qué punto está la devolución de una factura. */
export const ESTADO_DE_LA_DEVOLUCION = {
  SIN_ABONAR: "sinAbonar",
  PARCIAL: "parcial",
  COMPLETA: "completa",
};

/**
 * Reparte entre las líneas de la factura lo que queda disponible del producto.
 *
 * Una factura puede traer el **mismo artículo en más de una línea** -- el
 * terminal las junta al escanear, pero una factura traída de Business Central
 * llega como la guardó BC, y ahí sí se repiten. Dándole a cada línea el
 * disponible entero del producto, dos líneas de un artículo con una unidad
 * disponible proponen devolver una unidad **cada una**: dos, el doble de lo que
 * hay. Por eso se consume en orden, y la línea que llega cuando ya no queda nada
 * se queda en cero.
 *
 * @param {Array} lineas Las líneas de la factura original.
 * @param {Object} restantesPorProducto Lo que devuelve `getRemainingQuantitiesForInvoice`.
 * @returns {Array<{linea: object, originalQuantity: number, alreadyAbonedQuantity: number, remainingQuantity: number}>}
 */
export const repartirLoQueQueda = (lineas, restantesPorProducto) => {
  const porRepartir = {};

  return (lineas || []).map((linea) => {
    const cantidadDeLaLinea = roundQuantity(Math.abs(linea?.Quantity || 0));
    const clave = linea?.No;
    const delProducto = restantesPorProducto?.[clave];

    // Sin dato del producto no se inventa nada: la línea vale por sí misma,
    // que es el respaldo que ya usaba la pantalla.
    if (!delProducto) {
      return {
        linea,
        originalQuantity: cantidadDeLaLinea,
        alreadyAbonedQuantity: 0,
        remainingQuantity: cantidadDeLaLinea,
      };
    }

    if (!(clave in porRepartir)) {
      porRepartir[clave] = Math.max(delProducto.remainingQuantity || 0, 0);
    }

    const paraEsta = roundQuantity(Math.min(cantidadDeLaLinea, porRepartir[clave]));
    porRepartir[clave] = roundQuantity(porRepartir[clave] - paraEsta);

    return {
      linea,
      originalQuantity: cantidadDeLaLinea,
      alreadyAbonedQuantity: roundQuantity(cantidadDeLaLinea - paraEsta),
      remainingQuantity: paraEsta,
    };
  });
};

/**
 * En qué punto está la devolución, mirando el disponible de todos sus productos.
 *
 * «Completa» se mantiene tal cual estaba --no queda nada por devolver--, incluida
 * la factura de la que no se conocen líneas: ahí no queda nada que se pueda
 * comprobar, y proponer una devolución a ciegas es peor que no proponer ninguna.
 *
 * Lo nuevo es distinguir **parcial**: ya se abonó algo y todavía queda. El
 * listado no podía decirlo, porque sólo guardaba «completa o no», y una factura
 * con una nota de crédito parcial se veía igual que una intacta.
 *
 * @param {Object} restantesPorProducto Lo que devuelve `getRemainingQuantitiesForInvoice`.
 * @returns {"sinAbonar"|"parcial"|"completa"}
 */
export const estadoDeLaDevolucion = (restantesPorProducto) => {
  const productos = Object.values(restantesPorProducto || {});

  const quedaAlgo = productos.some((p) => (p?.remainingQuantity || 0) > 0);
  if (!quedaAlgo) return ESTADO_DE_LA_DEVOLUCION.COMPLETA;

  const yaSeAbonoAlgo = productos.some((p) => (p?.alreadyAbonedQuantity || 0) > 0);
  return yaSeAbonoAlgo ? ESTADO_DE_LA_DEVOLUCION.PARCIAL : ESTADO_DE_LA_DEVOLUCION.SIN_ABONAR;
};

/**
 * Cuánto puede diferir el importe antes de darlo por excedido: 0,02, la misma
 * tolerancia con que el terminal da por cerrada una venta.
 *
 * Hace falta porque la factura y sus abonos se redondean línea a línea: tres
 * abonos de una unidad pueden sumar un centavo más que la línea de tres
 * unidades que devuelven, sin que ninguno se pase.
 */
export const TOLERANCIA_DEL_IMPORTE_POR_ABONAR = 0.02;

/**
 * El producto de un abono que se pasa de lo que queda disponible, si lo hay.
 *
 * Se suma **por producto** antes de comparar. Comparando línea por línea, un
 * abono con el mismo artículo repetido pasa el control dos veces con la misma
 * unidad disponible y devuelve el doble.
 *
 * Se compara la cantidad **y** el importe con IVA: con el mismo artículo en
 * líneas de precios distintos, elegir la cara cabe por unidades y se pasa por
 * importe (`VNR15-000318`). Es la misma regla que aplica Business Central al
 * registrar desde la 1.0.4.112, y la del terminal avisa antes de guardar.
 * Acuerdo `contrato-hallazgos-ciclo-cri-2026-09-14.md`, H2 · TPV.
 *
 * Si lo disponible no trae importe --lo calculó alguien que sólo mira
 * cantidades--, sólo se compara la cantidad, como antes.
 *
 * @param {Array} lineasDelAbono
 * @param {Object} restantesPorProducto Lo que devuelve `getRemainingQuantitiesForInvoice`.
 * @param {object} [country] El país activo, para la precisión y el formato.
 * @returns {{No: string, pedido: number, disponible: number, importePedido: number,
 *   importeDisponible: number|null, porImporte: boolean, linea: object, aviso: string}|null}
 */
export const loQueSePasaDeLoDisponible = (lineasDelAbono, restantesPorProducto, country = null) => {
  const decimales = resolveMoneyDecimals(country);
  // `moneyEquals` recibe la tolerancia en unidades menores de la precisión de cálculo.
  const tolerancia = Math.round(TOLERANCIA_DEL_IMPORTE_POR_ABONAR * 10 ** decimales);
  const pedidoPorProducto = {};

  for (const linea of lineasDelAbono || []) {
    const clave = linea?.No;
    const pedido = roundQuantity((pedidoPorProducto[clave]?.pedido || 0) + Math.abs(linea?.Quantity || 0));
    const importePedido = addMoney(
      pedidoPorProducto[clave]?.importePedido || 0,
      importeConIvaDeLaLinea(linea, country),
      decimales
    );
    pedidoPorProducto[clave] = { pedido, importePedido, linea: pedidoPorProducto[clave]?.linea || linea };
  }

  for (const [clave, { pedido, importePedido, linea }] of Object.entries(pedidoPorProducto)) {
    const delProducto = restantesPorProducto?.[clave];
    const disponible = delProducto?.remainingQuantity ?? 0;
    const importeDisponible = Number.isFinite(delProducto?.remainingAmount)
      ? delProducto.remainingAmount
      : null;

    const porCantidad = pedido > disponible;
    const porImporte =
      importeDisponible !== null &&
      importePedido > importeDisponible &&
      !moneyEquals(importePedido, importeDisponible, tolerancia, decimales);

    if (porCantidad || porImporte) {
      const exceso = {
        No: clave,
        pedido,
        disponible,
        importePedido,
        importeDisponible,
        porImporte: !porCantidad,
        linea,
      };
      return { ...exceso, aviso: avisoDeLoQueSePasa(exceso, country) };
    }
  }

  return null;
};

/**
 * El aviso para el cajero cuando la nota de crédito se pasa de lo disponible.
 *
 * Lo que devuelve la nota de crédito se dice igual que lo que queda --unidades e
 * importe--, porque cuando el exceso es por importe la cantidad cabe, y sin el
 * importe el aviso no explicaría nada.
 */
const avisoDeLoQueSePasa = ({ No, pedido, disponible, importePedido, importeDisponible, linea }, country) => {
  const nombre = linea?.Description || No;
  const importe = (valor) => formatNumberByCountry(valor, country) || String(valor);
  const cantidad = (valor) =>
    String(roundQuantity(valor)).replace(".", country?.decimalSeparator || ".");
  const queda =
    importeDisponible === null
      ? `${cantidad(disponible)} u.`
      : `${cantidad(disponible)} u. por ${importe(Math.max(importeDisponible, 0))}`;

  return (
    `Del producto ${nombre} quedan por devolver ${queda}, y esta nota de crédito devuelve ` +
    `${cantidad(pedido)} u. por ${importe(importePedido)}. ` +
    "Ajusta la cantidad o elige la línea con el precio correcto."
  );
};

/**
 * El estado que le toca a una fila del listado.
 *
 * El listado lo recalcula al cargar --en la misma pasada en la que ya lee las
 * líneas y los pagos de todas las facturas--, así que lo normal es que la fila
 * llegue con el estado ya puesto. Cuando ese cálculo no se pudo hacer se cae a
 * lo que la factura tenga guardado, que es lo que escribe el servicio cada vez
 * que se crea, se guarda o se borra un abono.
 *
 * @param {{_devolucion?: string, fullyReturned?: boolean, partiallyReturned?: boolean}} factura
 * @returns {"sinAbonar"|"parcial"|"completa"}
 */
export const comoEstaLaDevolucion = (factura) => {
  if (factura?._devolucion) return factura._devolucion;
  if (factura?.fullyReturned) return ESTADO_DE_LA_DEVOLUCION.COMPLETA;
  if (factura?.partiallyReturned) return ESTADO_DE_LA_DEVOLUCION.PARCIAL;
  return ESTADO_DE_LA_DEVOLUCION.SIN_ABONAR;
};
